import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

interface MarkdownContentProps {
  content: string;
  loading: boolean;
  error: string | null;
}

export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  loading,
  error,
}) => {
  // Modal state and handlers for full-screen image preview (moved above early returns)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState<string | null>(null);
  const [modalAlt, setModalAlt] = useState<string>('');
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setModalOpen(false);
    }

    if (modalOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKey);
      // focus close button so keyboard users can easily dismiss
      setTimeout(() => closeButtonRef.current?.focus(), 0);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [modalOpen]);

  const openModal = (src?: string | null, alt?: string) => {
    if (!src) return;
    setModalSrc(src);
    setModalAlt(alt || '');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalSrc(null);
    setModalAlt('');
  };

  if (loading) {
    return (
      <div className='loading-container'>
        <div className='loading-spinner w-8 h-8'></div>
        <span className='loading-text'>Loading content...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className='status-message status-error'>
        <div className='flex'>
          <div className='flex-shrink-0'>
            <svg
              className='h-5 w-5 text-current'
              viewBox='0 0 20 20'
              fill='currentColor'
            >
              <path
                fillRule='evenodd'
                d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <div className='ml-3'>
            <h3 className='text-sm font-medium'>Error loading content</h3>
            <div className='mt-2 text-sm'>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='prose prose-lg max-w-none'>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Customize heading styles
          h1: ({ children }) => <h1 className='heading-1'>{children}</h1>,
          h2: ({ children }) => <h2 className='heading-2'>{children}</h2>,
          h3: ({ children }) => <h3 className='heading-3'>{children}</h3>,
          h4: ({ children }) => <h4 className='heading-4'>{children}</h4>,
          // Customize paragraph styles
          p: ({ children }) => <p className='text-body'>{children}</p>,
          // Customize list styles
          ul: ({ children }) => (
            <div className='list-styled'>
              <ul>{children}</ul>
            </div>
          ),
          ol: ({ children }) => (
            <div className='list-styled'>
              <ol>{children}</ol>
            </div>
          ),

          li: ({ children }) => <li>{children}</li>,
          // Customize blockquote styles
          blockquote: ({ children }) => (
            <blockquote className='blockquote'>{children}</blockquote>
          ),
          // Customize code styles
          code: ({ children, className }) => {
            const isInline = !className;
            if (isInline) {
              return <code className='code-inline'>{children}</code>;
            }
            return <code className='code-block'>{children}</code>;
          },
          hr: () => <hr className='divider-xsm' />,
          // Customize link styles
          a: ({ href, children }) => (
            <a
              href={href}
              className='link'
              target='_blank'
              rel='noopener noreferrer'
            >
              {children}
            </a>
          ),
          // Customize image styles (render image inside a button for accessibility)
          img: ({ src, alt, title }) => {
            // Determine the image class based on the title attribute
            let imageClass = 'markdown-image';

            if (title) {
              const titleLower = title.toLowerCase();

              // Size classes
              if (titleLower.includes('small')) imageClass += ' image-small';
              else if (titleLower.includes('medium'))
                imageClass += ' image-medium';
              else if (titleLower.includes('large'))
                imageClass += ' image-large';
              else if (titleLower.includes('full')) imageClass += ' image-full';

              // Layout classes
              if (titleLower.includes('float-left')) {
                imageClass += ' image-float-left';
              } else if (titleLower.includes('float-right')) {
                imageClass += ' image-float-right';
              } else if (!titleLower.includes('gallery')) {
                // Only center if not explicitly in a gallery
                imageClass += ' image-center';
              }

              // Special types
              if (titleLower.includes('screenshot'))
                imageClass += ' screenshot';
              if (titleLower.includes('phone'))
                imageClass += ' phone-screenshot';
              if (titleLower.includes('desktop'))
                imageClass += ' desktop-screenshot';
              if (titleLower.includes('step')) imageClass += ' step-image';
              if (titleLower.includes('icon')) imageClass = 'icon-image';
            } else {
              // Default to center if no title
              imageClass += ' image-center';
            }

            return (
              <button
                type='button'
                className={`p-0 bg-transparent border-0 ${imageClass} cursor-pointer`}
                onClick={() => openModal(src, alt)}
                aria-label={
                  alt ? `Open image: ${alt}` : 'Open image in fullscreen'
                }
              >
                <img
                  src={src}
                  alt={alt || ''}
                  title={title}
                  className='w-full h-auto'
                  loading='lazy'
                />
              </button>
            );
          },
          // Customize table styles
          table: ({ children }) => <table className='table'>{children}</table>,
          th: ({ children }) => <th className='table-header'>{children}</th>,
          td: ({ children }) => <td className='table-cell'>{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>

      {/* Full-screen modal for image preview */}
      {modalOpen && modalSrc && (
        <div className='fixed inset-0 z-50 flex items-center justify-center'>
          {/* interactive backdrop button covers the entire screen and closes modal */}
          <button
            type='button'
            className='absolute inset-0 bg-black/75 backdrop-blur-xl z-40'
            onClick={closeModal}
            aria-label='Close image preview'
          />

          {/* close button fixed to viewport top-right */}
          <button
            ref={closeButtonRef}
            className='cursor-pointer fixed top-4 right-4 z-[60] text-white text-3xl leading-none px-3 py-1 bg-transparent border-0'
            onClick={e => {
              e.stopPropagation();
              closeModal();
            }}
            aria-label='Close image'
            type='button'
          >
            ×
          </button>

          {/* modal content - placed above the backdrop */}
          <div
            className='relative z-50 flex items-center justify-center'
            role='dialog'
            aria-modal='true'
            aria-label={modalAlt || 'Image preview'}
          >
            <div className='p-0 bg-transparent border-0'>
              <img
                src={modalSrc}
                alt={modalAlt}
                className='max-w-[90vw] max-h-[90vh] object-contain shadow-lg'
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
